import { Box, Button, Fade, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  DownArrowIcon,
  NEW,
  cropClasses,
  cropPlanStatusChoices,
  fadeTimingMs,
  millisInDay,
  miscClasses,
  cardClasses,
  errFeedbackFn,
} from '../../../utility';
import { StrainAccordion } from '../../utils/StrainAccordion';
import Specificentrypopup from '../../Popup/Specificentrypopup';
import NoEntityMsg from '../../../utility/messages/NoEntityMsg';
import apiService from '../../../../api/apiService';

export default function _SpecificsTab(props) {
  const { cropplanid, cropPlanPerms, currDarkThemeClass, queryParam, values, handleSetValues, savedRes, handleErrorWithFeedback } = props;
  const [isSpecificEntryPopupOpen, setSpecificEntryPopupOpen] = useState(false);
  const [selectedFarmId, setSelectedFarmId] = useState(null);
  const [selectedFarmName, setSelectedFarmName] = useState('');
  const [filteredStrains, setFilteredStrains] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);

  const [unitsFetched, setUnitsFetched] = useState(false);

  const fetchUnits = async () => {
    setIsLoadingUnits(true);
    try {
      const url = 'unit/?sync_to_metrc=true';
      const response = await apiService.fetchInstance(url);

      // Better normalization logic
      let unitsData = [];

      // Check response.data first (this seems to be your API structure)
      if (response?.data) {
        if (Array.isArray(response.data.results)) {
          unitsData = response.data.results;
        } else if (Array.isArray(response.data)) {
          unitsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          unitsData = response.data.data;
        }
      }
      // Fallback to response.results
      else if (Array.isArray(response?.results)) {
        unitsData = response.results;
      }
      // Last fallback - direct array
      else if (Array.isArray(response)) {
        unitsData = response;
      }

      if (unitsData.length > 0) {
        const weightBasedUnits = unitsData.filter(u => u.unit_type === 'WeightBased');
        handleSetValues('units_of_measure', weightBasedUnits);
      } else {
        console.log('No units found in response');
        handleSetValues('units_of_measure', []);
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      handleErrorWithFeedback(errFeedbackFn({}))(error);
    } finally {
      setIsLoadingUnits(false);
    }
  };
  // Auto-fetch units when component mounts (if not already loaded)
  useEffect(() => {
    if (!values?.units_of_measure?.length && !isLoadingUnits) {
      fetchUnits();
    }
  }, []);
  useEffect(() => {
    const handleInitialFarm = () => {
      if (values?.farms?.length === 1 && !values?.farm) {
        const farm = values.farms[0];
        handleSetValues('farm', farm.id);
        setSelectedFarmId(farm.id);
        setSelectedFarmName(farm.farm_name);
      } else if (values?.farm) {
        const farm = values.farms?.find(f => f.id === values.farm);
        if (farm) {
          setSelectedFarmId(farm.id);
          setSelectedFarmName(farm.farm_name);
        }
      }
    };

    handleInitialFarm();
  }, [values?.farms, values?.farm]);

  useEffect(() => {
    if (selectedFarmId) {
      const strains = values?.farmstrains?.filter(strain => strain.farm === selectedFarmId) ?? [];
      setFilteredStrains(strains);
    } else {
      setFilteredStrains([]);
    }
  }, [selectedFarmId, values?.farmstrains]);

  const handleFarmChange = event => {
    const farmId = event.target.value;
    const farm = values.farms?.find(f => f.id === farmId);
    if (farm) {
      handleSetValues('farm', farmId);
      setSelectedFarmId(farmId);
      setSelectedFarmName(farm.farm_name);
    }
  };

  const cropPlanSpecificsTextFieldProps = ({ prop, inputProps = {}, otherSxProps = {}, otherInputProps = {}, ...otherTextProps }) => ({
    autoComplete: 'off',
    className: `${cropClasses?.textField} ${currDarkThemeClass}`,
    disabled:
      (cropplanid !== NEW && !cropPlanPerms?.update) ||
      values?.processing ||
      (prop === 'target_subphase_date' && cropplanid !== NEW) ||
      (prop === 'phaseplan' && cropplanid !== NEW) ||
      (prop === 'qualityplan' && cropplanid !== NEW) ||
      (prop === 'target_subphase' && cropplanid !== NEW) ||
      (prop === 'unit' && cropplanid !== NEW),
    inputProps,
    InputLabelProps: {
      ...((prop === 'target_subphase_date' || prop === 'description') && {
        shrink: true,
      }),
    },
    InputProps: {
      ...otherInputProps,
    },
    label: ' ',
    onChange: ({ target: { value } }) => handleSetValues(prop, value),
    required: true,
    sx: {
      ...otherSxProps,
    },
    value: values?.[prop] ?? '',
    ...otherTextProps,
  });

  const cropPlanSpecificsBoxProps = {
    className: `${cropClasses?.specificsBox} ${currDarkThemeClass}`,
  };

  const stackwidthProps = {
    className: `${cropClasses?.specificsstack} ${currDarkThemeClass}`,
  };

  const openSpecificEntryPopup = () => {
    setSpecificEntryPopupOpen(true);
  };

  const closeSpecificEntryPopup = () => {
    setSpecificEntryPopupOpen(false);
  };
  const nameArgs = {
    label: 'Name',
    prop: 'name',
    autoFocus: true,
  };

  const descArgs = {
    label: 'Description',
    prop: 'description',
    required: false,
  };

  const cropPlanMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const phasePlans = values?.phaseplans ?? {};

  const phasePlansList =
    values?.phaseplans?.map((e, i) => (
      <MenuItem key={i} value={e?.id} {...cropPlanMenuItemProps}>
        {e?.name} ({e?.total_days} Days)
      </MenuItem>
    )) ?? [];

  if (!phasePlansList?.length) {
    phasePlansList.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Phase Plans
      </MenuItem>
    );
  }

  const phaseplanArgs = {
    label: 'Phase Plan',
    prop: 'phaseplan',
    type: 'number',
    select: true,
    SelectProps: {
      IconComponent: DownArrowIcon,
    },
    onChange: ({ target: { value } }) => {
      handleSetValues('phaseplan', value);
      handleSetValues('qualityplan', null);
      handleSetValues('target_subphase', null);
      handleSetValues('strains', []);
    },
  };
  const selectedPhasePlan = values?.phaseplans?.find(
    plan => plan.id === values?.phaseplan
  );
  // console.log("selected phaseplan",selectedPhasePlan)

  const tragetSubphasesList = selectedPhasePlan?.subphases?.map((sub, i) => (
    <MenuItem key={i} value={sub?.id} {...cropPlanMenuItemProps}>
      {sub?.name}
    </MenuItem>
  )) ?? [
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Subphases
      </MenuItem>,
    ];
  // const tragetSubphasesList =
  //   values?.phaseplans?.subphases?.map((e,i)=>(
  //     <MenuItem key={i} value={e?.id} {...cropPlanMenuItemProps}>
  //       {e?.name}
  //     </MenuItem>
  //   )) ?? [];

  const subphaseArgs = {
    label: 'Target Subphase',
    prop: 'target_subphase',
    type: 'number',
    select: true,
    SelectProps: {
      IconComponent: DownArrowIcon,
    },
  };
  const filteredQualityPlans = selectedPhasePlan
    ? values?.qualityplans?.filter(
      qp => qp.phaseplan === selectedPhasePlan.id
    )
    : [];
  // console.log("filteredQualityPlans",filteredQualityPlans)
  const qualityPlansLists =
    values?.qualityplans?.map((e, i) => (
      <MenuItem key={i} value={e?.id} {...cropPlanMenuItemProps}>
        {e?.name}
      </MenuItem>
    )) ?? [];

  if (!qualityPlansLists?.length) {
    qualityPlansLists.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Quality Plans
      </MenuItem>
    );
  }
  const qualityPlansList =
    // filteredQualityPlans.length > 0
    filteredQualityPlans.map((e, i) => (
      <MenuItem key={i} value={e?.id} {...cropPlanMenuItemProps}>
        {e?.name}
      </MenuItem>
    ))
    ?? [];
  if (!qualityPlansList?.length) {
    qualityPlansList.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Quality Plans
      </MenuItem>
    );
  }




  const qualityplanArgs = {
    label: 'Quality Plan',
    prop: 'qualityplan',
    type: 'number',
    select: true,
    SelectProps: {
      IconComponent: DownArrowIcon,
    },
  };
  const serDateArgs = {
    label: 'Target Subphase Date ',
    type: 'date',
    prop: 'target_subphase_date',
  };
  const isNew = cropplanid === NEW;
  const filteredChoices = isNew ? [cropPlanStatusChoices[0]] : cropPlanStatusChoices;
  const planStatusList = filteredChoices.map((choice, i) => (
    <MenuItem key={i} value={choice?.value} {...cropPlanMenuItemProps}>
      {choice.label}
    </MenuItem>
  ));

  const planStatusProps = {
    label: 'Status',
    prop: 'run_status',
    select: true,
    SelectProps: {
      IconComponent: DownArrowIcon,
    },
  };

  const unitList =
    Array.isArray(values?.units_of_measure) && values.units_of_measure.length > 0
      ? values.units_of_measure.map((e, i) => (
        <MenuItem key={i} value={e?.id} {...cropPlanMenuItemProps}>
          {e?.unit_name}
        </MenuItem>
      ))
      : [];

  if (!unitList?.length) {
    unitList.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        {unitsFetched ? 'No Units Available' : 'No Available Units'}
      </MenuItem>,
      <MenuItem
        key={-2}
        value={-2}
        {...cropPlanMenuItemProps}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          fetchUnits();
        }}
        disabled={isLoadingUnits}
        sx={{
          color: 'primary.main',
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        {isLoadingUnits ? 'Fetching...' : 'Fetch Units'}
      </MenuItem>
    );
  }
  const unitProps = {
    label: 'Unit',
    prop: 'unit',
    select: true,
    SelectProps: {
      IconComponent: DownArrowIcon,
    },
  };

  const usersList =
    values?.users?.map((e, i) => (
      <MenuItem key={i} value={e?.id} {...cropPlanMenuItemProps}>
        {e?.first_name}
      </MenuItem>
    )) ?? [];

  if (!usersList?.length) {
    usersList.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Users
      </MenuItem>
    );
  }

  const ownerargs = {
    label: 'Owner',
    type: 'number',
    select: true,
    disabled: cropplanid !== NEW,
    SelectProps: {
      IconComponent: DownArrowIcon,
    },
    prop: 'owner',
  };

  const farmList =
    values?.farms?.map((e, i) => (
      <MenuItem key={i} value={e?.id} {...cropPlanMenuItemProps} disabled={cropplanid !== NEW}>
        {e?.farm_name}
      </MenuItem>
    )) ?? [];

  if (!farmList.length) {
    farmList.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Farms
      </MenuItem>
    );
  }

  const addStrainTypoProps = {
    className: `${cropClasses?.titleTypo} ${currDarkThemeClass}`,
  };

  const expandCardProps = e => ({
    strain: e,
    currDarkThemeClass,
    selectedFarmId,
    values,
    handleSetValues,
    qualityPlansList,
    qualityPlansLists,
    phasePlansList,
    phasePlans,
    handleErrorWithFeedback,
    cropPlanPerms,
    tragetSubphasesList,
    target_subphase_date: values?.target_subphase_date,
  });

  const strainsList = values?.strains?.map((strain, i) => <StrainAccordion key={i} {...expandCardProps(strain)} />);

  const handleAddStrain = payload => handleSetValues('strains', [...(values?.strains ?? []), payload]);

  const sEntryPopupProps = {
    selectedFarmId,
    selectedFarmName,
    open: isSpecificEntryPopupOpen,
    onClose: closeSpecificEntryPopup,
    currDarkThemeClass,
    target_subphase_date: values?.target_subphase_date,
    target_subphase: values?.target_subphase,
    strainCount: values?.strains?.length ?? 0,
    // plantCount: values.plant_count,
    qualityplan: values?.qualityplan ?? -1,
    unit: values?.unit,
    phaseplan: values?.phaseplan ?? -1,
    handleAddStrain: handleAddStrain,
    parentValues: values,
    handleErrorWithFeedback,
    filteredStrains,
  };

  const farmField = () => {
    const isSingleFarm = values?.farms?.length === 1;
    if (isSingleFarm) {
      return (
        <TextField
          label="Farm"
          value={selectedFarmName}
          disabled
          className={`${cropClasses?.textField} ${currDarkThemeClass}`}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    const farmList =
      values?.farms?.map(farm => (
        <MenuItem key={farm.id} value={farm.id} className={`${cropClasses?.menuItem} ${currDarkThemeClass}`} disabled={cropplanid !== NEW}>
          {' '}
          {farm.farm_name} ({farm.license_number})
        </MenuItem>
      )) ?? [];
    if (!farmList.length) {
      farmList.push(
        <MenuItem key={-1} value={-1} disabled className={`${cropClasses?.menuItem} ${currDarkThemeClass}`}>
          No Available Farms
        </MenuItem>
      );
    }
    return (
      <TextField
        label="Farm"
        value={values?.farm ?? ''}
        onChange={handleFarmChange}
        className={`${cropClasses?.textField} ${currDarkThemeClass}`}
        select
        disabled={cropplanid !== NEW}
        SelectProps={{
          IconComponent: DownArrowIcon,
        }}
        InputLabelProps={{ shrink: true }}
      >
        {farmList}
      </TextField>
    );
  };

  return (
    <Fade in={true} timeout={fadeTimingMs}>
      <Box {...cropPlanSpecificsBoxProps}>
        <TextField {...cropPlanSpecificsTextFieldProps(nameArgs)} />
        <TextField {...cropPlanSpecificsTextFieldProps(descArgs)} />
        <Box {...stackwidthProps}>
          <Stack direction="row" spacing={2}>
            <TextField {...cropPlanSpecificsTextFieldProps(phaseplanArgs)}>{phasePlansList}</TextField>
            <TextField {...cropPlanSpecificsTextFieldProps(qualityplanArgs)}>{qualityPlansList}</TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField {...cropPlanSpecificsTextFieldProps(subphaseArgs)}>{tragetSubphasesList}</TextField>
            <TextField {...cropPlanSpecificsTextFieldProps(serDateArgs)} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField {...cropPlanSpecificsTextFieldProps(ownerargs)}>{usersList}</TextField>
            <TextField {...cropPlanSpecificsTextFieldProps(planStatusProps)}>{planStatusList}</TextField>

            {/* <TextField
              {...cropPlanSpecificsTextFieldProps(plantCtargs)}
              value={values.plant_count || ''}
              onChange={e => handleSetValues('plant_count', e.target.value)}
            /> */}
          </Stack>
          <Stack direction="row" spacing={2}>
            {farmField()}
            <TextField {...cropPlanSpecificsTextFieldProps(unitProps)}>{unitList}</TextField>
          </Stack>
        </Box>

        {/* {values?.date_of_serialization && values?.plant_count && selectedFarmId && ( */}
        {values?.target_subphase_date && values?.qualityplan && selectedFarmId && (
          <>
            <Stack direction="row" alignItems="center" marginTop={4} justifyContent="space-between">
              <Typography {...addStrainTypoProps}>Strains</Typography>
              {/* <Button {...addStrainsBtnProps} /> */}
            </Stack>
            {!strainsList?.length ? (
              <NoEntityMsg entity="Strain" isCreateEnabled={cropPlanPerms?.update} onCreateClick={() => openSpecificEntryPopup()} />
            ) : (
              strainsList
            )}
            {isSpecificEntryPopupOpen && <Specificentrypopup {...sEntryPopupProps} />}
          </>
        )}
      </Box>
    </Fade>
  );
}
